import type { FunnelKpis } from './types';
import { Info } from 'lucide-react';

function Tile({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5 bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 min-w-[120px]">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-lg font-semibold text-super-dark-purple">{value}</p>
      {hint && <p className="text-[10px] text-gray-400 leading-snug">{hint}</p>}
    </div>
  );
}

function ChipRow({ title, counts }: { title: string; counts: Record<string, number> }) {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
      <div className="flex flex-wrap gap-2">
        {entries.map(([k, n]) => (
          <span key={k} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-3.5 py-1.5 text-xs">
            <b className="text-super-dark-purple">{n}</b>
            <span className="text-gray-500">{k}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function FunnelDashboard({ funnel, onOpenImports }: { funnel: FunnelKpis; onOpenImports: () => void }) {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        {funnel.note} {funnel.as_of ? `Baseline as of ${funnel.as_of}.` : 'No dated baseline yet.'}
      </div>

      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Coverage funnel</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          <Tile label="Assessed" value={funnel.assessed} hint="Phase 2 Application Details" />
          <Tile label="Out of scope" value={funnel.out_of_scope} />
          <Tile label="Cloud candidates" value={funnel.cloud_candidates} />
          <Tile label="On-prem denom" value={funnel.on_prem_candidates} hint="50% target denominator" />
          <Tile label="In progress" value={funnel.in_progress} hint="Phase 2 progress" />
          <Tile label="Cloud deployed" value={funnel.migrated} />
          <Tile label="Needs decision" value={funnel.needs_decision} hint="Evaluation required" />
          <Tile label="Phase 3 rows" value={funnel.phase3.rows} hint={`${funnel.phase3.hidden} were hidden in Excel`} />
        </div>
      </div>

      {funnel.scope_conflicts > 0 && (
        <div className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-3 py-2.5">
          {funnel.scope_conflicts} EAI{funnel.scope_conflicts === 1 ? '' : 's'} appear on both Application Details and Out of Scope
          {funnel.scope_conflict_eais.length ? ` (${funnel.scope_conflict_eais.slice(0, 8).join(', ')}${funnel.scope_conflict_eais.length > 8 ? '…' : ''})` : ''}.
          Both facts are kept — neither is silently dropped.
        </div>
      )}

      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">50% cloud target</p>
        <div className="flex flex-wrap gap-2">
          <span className="bg-gray-50 border border-gray-200 rounded-full px-3.5 py-1.5 text-xs">
            <b className="text-super-dark-purple">{funnel.target_50pct.pct == null ? '—' : `${funnel.target_50pct.pct}%`}</b>
            <span className="text-gray-500 ml-2">deployed / on-prem candidates ({funnel.target_50pct.denominator})</span>
          </span>
        </div>
        <p className="text-[11px] text-gray-400 mt-1.5">{funnel.target_50pct.note}</p>
      </div>

      <ChipRow title="BUY / HOLD / SELL (SoR / GRC)" counts={funnel.by_disposition} />
      <ChipRow title="Cloud category 0–3 (Phase 2)" counts={funnel.by_category} />
      <ChipRow title="Phase 2 cloud migration progress" counts={funnel.by_progress} />
      <ChipRow title="Primary cloud disposition" counts={funnel.by_primary_cloud} />
      <ChipRow title="Phase 3 current status" counts={funnel.phase3.by_status} />
      <ChipRow title="Phase 3 entity types" counts={funnel.phase3.by_entity_type} />
      <ChipRow title="Phase 3 category 0–3" counts={funnel.phase3.by_category} />

      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Impact / benefits</p>
        <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {funnel.impact.available
            ? 'Victor footprint rows are present.'
            : (funnel.impact.message || 'Victor infrastructure extract is not imported yet — no VM, Oracle, or cost figures.')}
        </div>
      </div>

      <p className="text-[11px] text-gray-400">
        Universe (~2,700 EAI) is not in this baseline — import a ServiceNow extract later.{' '}
        <button type="button" onClick={onOpenImports} className="text-amber-700 font-semibold hover:underline">Open the Data menu</button>
      </p>
    </div>
  );
}
